import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api, track, errorText } from "../lib/api";

const QUESTIONS = [
  { c: "Financial Control", q: "We can see cash available and obligations for the next 13 weeks." },
  { c: "Financial Control", q: "We know the true contribution or profit by truck." },
  { c: "Financial Control", q: "We review receivables, debt, taxes, insurance, and reserves on a consistent rhythm." },
  { c: "Fleet Economics & Utilization", q: "We know which trucks, lanes, and customers create or destroy margin." },
  { c: "Fleet Economics & Utilization", q: "We track utilization, deadhead, downtime, and lost-revenue causes." },
  { c: "Fleet Economics & Utilization", q: "Equipment growth, replacement, and exit decisions are based on numbers rather than pressure." },
  { c: "Maintenance Systems", q: "Preventive maintenance is scheduled, tracked, and completed consistently." },
  { c: "Maintenance Systems", q: "We maintain a repair reserve or forward maintenance forecast." },
  { c: "Maintenance Systems", q: "We review repeat failures, downtime patterns, and repair cost by unit." },
  { c: "Drivers & People", q: "Drivers receive clear onboarding, expectations, and communication standards." },
  { c: "Drivers & People", q: "Performance and accountability are reviewed on a weekly rhythm." },
  { c: "Drivers & People", q: "We understand why drivers stay or leave and address repeat patterns." },
  { c: "Owner Independence & Management", q: "The company runs a weekly operating review using a scorecard." },
  { c: "Owner Independence & Management", q: "Key processes and decisions do not live only inside the owner's head." },
  { c: "Owner Independence & Management", q: "The owner can step away for two business days without the operation losing control." },
];

const SCALE = [
  { v: 1, label: "Not true / no visibility" },
  { v: 2, label: "Rarely / mostly reactive" },
  { v: 3, label: "Partly true / inconsistent" },
  { v: 4, label: "Usually true / controlled" },
  { v: 5, label: "Fully true / repeatable" },
];

function assessmentStepLabel(step) {
  if (step < 0) return "Fleet Crossroads Scorecard";
  if (step < 15) return `Fleet Crossroads Scorecard · ${step + 1} of 15`;
  return "Almost done — where should we send your result?";
}

export default function FleetHealthAssessment() {
  const nav = useNavigate();
  const [step, setStep] = useState(-1); // -1 = intro, 0..14 = questions, 15 = gate form
  const [answers, setAnswers] = useState(Array(15).fill(null));
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "",
    active_trucks: "", annual_revenue: "", years_in_business: "",
    mobile: "", sms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const progress = step < 0 ? 0 : Math.round(((step + 1) / 16) * 100);

  const setAnswer = (v) => {
    const next = [...answers]; next[step] = v; setAnswers(next);
    track("assessment_question_progress", "/fleet-crossroads-scorecard", { q: step + 1, v });
    setTimeout(() => setStep(step + 1), 180);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const res = await api.post("/assessment/submit", { ...form, answers });
      track("assessment_complete", "/fleet-crossroads-scorecard",
        { total: res.data.total_score, band: res.data.result_band });
      nav(`/fleet-crossroads-results?rid=${res.data.result_id}`);
    } catch (err) {
      setError(errorText(err, "Submission failed. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Layout minimal>
      <SEO title="Fleet Crossroads Scorecard | Driver Reborn Advisory" description="Assess the financial visibility, operational systems, and owner dependence behind your trucking fleet and identify the area requiring the most attention." />
      <section className="section">
        <div className="container-tight">
          <div className="dr-progress-track mb-8" data-testid="assessment-progress">
            <div className="dr-progress-fill" style={{ width: `${progress}%` }}/>
          </div>
          <div className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">
            {assessmentStepLabel(step)}
          </div>

          {step === -1 && (
            <div>
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase leading-tight mb-6" data-testid="assessment-intro-title">
                See Where Your Fleet Is Losing Clarity, Control, or Scale Readiness.
              </h1>
              <p className="text-[#E6E6E6] leading-relaxed mb-4 max-w-2xl">
                The Fleet Crossroads Scorecard is a free, structured educational screening tool for established trucking-company owners. It examines five areas that determine whether the company is becoming more controlled — or simply becoming a larger version of the same pressure:
              </p>
              <ul className="grid sm:grid-cols-2 gap-2 mb-6 max-w-2xl" data-testid="assessment-intro-areas">
                {[
                  "Financial control",
                  "Fleet economics and utilization",
                  "Maintenance systems",
                  "Drivers and people systems",
                  "Owner independence and management accountability",
                ].map((a) => (
                  <li key={a} className="flex items-start gap-2 text-[#E6E6E6] text-sm">
                    <span className="text-[#FF6A00] font-bebas mt-0.5 flex-shrink-0">■</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-8 max-w-2xl" data-testid="assessment-intro-disclaimer">
                The Assessment is not a diagnosis and does not guarantee revenue, profit, savings, financing, loads, or business outcomes. It helps you recognize patterns and determine whether deeper professional review may be warranted. 15 questions · about 5–7 minutes.
              </p>
              <button onClick={() => setStep(0)} className="btn-primary" data-testid="assessment-begin">
                Begin the Assessment <ArrowRight size={20}/>
              </button>
            </div>
          )}

          {step >= 0 && step < 15 && (
            <div>
              <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2" data-testid={`assessment-cat-${step}`}>
                {QUESTIONS[step].c}
              </div>
              <h1 className="font-bebas text-3xl sm:text-4xl uppercase leading-tight mb-6" data-testid={`assessment-q-${step}`}>
                {QUESTIONS[step].q}
              </h1>
              <div className="grid gap-3">
                {SCALE.map(s => (
                  <button key={s.v} onClick={() => setAnswer(s.v)}
                    data-testid={`assessment-a-${step}-${s.v}`}
                    className={`text-left border ${answers[step] === s.v ? "border-[#FF6A00] text-[#FF6A00]" : "border-[#2B2B2B] text-[#E6E6E6]"} bg-[#111111] hover:border-[#FF6A00] px-5 py-4 uppercase font-bebas text-xl tracking-wide transition-colors`}>
                    <span className="text-[#FF6A00] mr-3">{s.v}</span> {s.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button className="btn-ghost mt-6" onClick={()=>setStep(step-1)} data-testid="assessment-back">
                  <ArrowLeft size={16}/> Back
                </button>
              )}
            </div>
          )}

          {step === 15 && (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-bebas text-3xl sm:text-4xl uppercase leading-tight mb-6">
                See My Scorecard Result
              </h2>
              {[
                { k: "first_name", l: "First name", req: true },
                { k: "last_name", l: "Last name", req: true },
                { k: "email", l: "Business email", type: "email", req: true },
                { k: "phone", l: "Phone number", type: "tel", req: true },
                { k: "company", l: "Business name", req: true },
              ].map(f => (
                <div key={f.k}>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">{f.l}</label>
                  <input type={f.type || "text"} required={f.req} value={form[f.k]}
                    onChange={e=>setForm({...form, [f.k]: e.target.value})}
                    data-testid={`assessment-${f.k}`}
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Active trucks</label>
                  <select required value={form.active_trucks} onChange={e=>setForm({...form, active_trucks: e.target.value})}
                    data-testid="assessment-active_trucks"
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none">
                    <option value="">Select</option>
                    {["1","2-3","4-5","5-10","11-20","20+"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Annual revenue</label>
                  <select required value={form.annual_revenue} onChange={e=>setForm({...form, annual_revenue: e.target.value})}
                    data-testid="assessment-annual_revenue"
                    className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none">
                    <option value="">Select</option>
                    {["Under $500K","$500K-$1M","$1M-$2M","$2M-$5M","Over $5M"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Years in business</label>
                <select required value={form.years_in_business} onChange={e=>setForm({...form, years_in_business: e.target.value})}
                  data-testid="assessment-years_in_business"
                  className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none">
                  <option value="">Select</option>
                  {["0-1","1-3","3-5","5-10","10+"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Mobile (optional)</label>
                <input type="tel" value={form.mobile} onChange={e=>setForm({...form, mobile: e.target.value})}
                  data-testid="assessment-mobile"
                  className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
              </div>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={form.sms_consent} onChange={e=>setForm({...form, sms_consent: e.target.checked})}
                  data-testid="assessment-sms" className="mt-1 accent-[#FF6A00]"/>
                <span className="text-[#E6E6E6]">Text me the result and next steps. (Reply STOP anytime.)</span>
              </label>
              {error && <div className="text-[#EF4444] text-sm" data-testid="assessment-error">{error}</div>}
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="assessment-submit">
                {submitting ? "Scoring…" : <>See My Scorecard Result <ArrowRight size={20}/></>}
              </button>
              <p className="text-xs text-[#A1A1AA]">
                This is an educational screening result. Not financial, legal, or compliance advice.
              </p>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
